'use strict';

/**
 * FocusQuery
 * ----------
 * Turn a list's filter state and sort into a meadow-filter string the SERVER can answer (WI #433).
 *
 * THE BUG THIS EXISTS FOR: the list fetched the first 250 rows and then sorted and filtered that array
 * in the browser. Sorting 1,075 blueprints descending by mark put 250 at the top and 1 at the bottom,
 * because 250 was the largest thing the client had ever been shown. Every sort was a sort of an
 * arbitrary quarter, and nothing on the screen said so.
 *
 * NEVER SILENTLY PARTIAL. That is the whole rule here. Anything this cannot express server-side is
 * reported in `Unexpressed` rather than dropped, so the caller can fetch the full set and filter it
 * locally instead. A filter quietly applied to one page is precisely the defect being fixed, and it
 * would be easy to reintroduce one facet at a time.
 *
 * THREE PROPERTIES OF THE DSL WERE ESTABLISHED BY GENERATING SQL, not by reading docs. Each one is a
 * silent wrong answer if you get it wrong, and each has a test.
 *
 * 1. FSF NEEDS FOUR TERMS. `FSF~Name~DESC` produces NO ORDER BY at all; `FSF~Name~DESC~` produces it.
 *    The parser walks the string four terms at a time, so a three-term stanza is never seen. A sort
 *    that silently does not sort is the same family of bug as the one being fixed.
 *
 * 2. AN UNWRAPPED OR ESCAPES THE TENANCY FILTER, and this one is a cross-tenant leak rather than a
 *    cosmetic fault. The server appends `IDCustomer` as an AND after whatever arrives, and AND binds
 *    tighter than OR:
 *
 *        WHERE "Title" LIKE :a OR "Description" LIKE :b AND "IDCustomer" = :c
 *
 *    reads as `Title LIKE a OR (Description LIKE b AND IDCustomer = c)`, so every row in the database
 *    whose Title matches comes back, whoever owns it. Wrapping the group fixes it:
 *
 *        WHERE ( "Title" LIKE :a OR "Description" LIKE :b ) AND "IDCustomer" = :c
 *
 *    So EVERY OR group this module emits is wrapped in FOP/FCP. There is no exception, and the test
 *    asserts the generated SQL rather than the generated string.
 *
 * 3. BLANKS CHANGE ENDS, and this is a real behaviour change rather than a bug to fix here. The old
 *    browser comparator coerced null to '', so empty rows sorted to the TOP ascending. Postgres is
 *    NULLS LAST on ASC and NULLS FIRST on DESC -- exactly inverted at both ends. Every column in this
 *    schema is nullable, so it can show anywhere, though in practice the sortable columns are stamped
 *    on write and hold few nulls.
 *
 *    Matching the old placement would mean emitting NULLS FIRST/LAST, which foxhound's ORDER BY
 *    builder does not support, so it is a change to a sibling module and a release of it. Not worth
 *    blocking this on: the server's placement is the standard one, and a row with no value in the
 *    column you sorted by has no obviously correct home. Recorded so the first person to notice finds
 *    an answer instead of a mystery.
 *
 * 4. FBD IS BROKEN. `FBD~UpdateDate~GT~2026-08-25` emits `WHERE "DATE(UpdateDate)" > :x` -- a quoted
 *    identifier naming a column that does not exist, so the query errors. Date windows use plain FBV.
 *
 * 5. A COLUMN THE ENTITY DOES NOT HAVE IS A 500, NOT A WRONG ANSWER. foxhound quotes whatever field
 *    name it is handed, so `FBV~Body~LK~%x%` against Blueprint compiles to `WHERE "Body" LIKE :x` and
 *    Postgres refuses the whole query. Of the twenty-one lists, exactly one (Feedback) has a Body
 *    column and exactly one (WorkItem) has both Title and Description, so a fixed search-field list
 *    would have failed on the FIRST keystroke of nearly every search box in the app. The same hazard
 *    reaches the facets by a longer road: filter state is restored from the URL hash and from saved
 *    preferences, neither of which is scoped to the screen, so a `priority=High` left in the hash can
 *    arrive at a list whose table has no Priority column.
 *
 *    So every field this emits is checked against what the DESCRIPTOR DECLARES (see _askable), and a
 *    field it cannot vouch for is reported in Unexpressed rather than sent. Declared, not guessed:
 *    the same lesson as the Computed check below, which exists because a naming convention was read
 *    as a contract once already.
 *
 * @license MIT
 * @author Steven Velozo <steven@velozo.com>
 */

// A stanza is four tilde-separated terms. Anything short is silently ignored by the parser, so this is
// the only way a stanza should ever be built.
function _stanza(pInstruction, pField, pOperator, pValue)
{
	return [pInstruction, pField, pOperator, String(pValue == null ? '' : pValue)].join('~');
}

/** Tildes separate stanzas, so a value carrying one would split into a different query entirely. */
function _safe(pValue)
{
	return String(pValue == null ? '' : pValue).replace(/~/g, ' ');
}

/**
 * Wrap a set of OR stanzas so the group binds before the server's tenancy AND.
 * Returns '' for an empty group rather than an empty paren pair, which parses to nothing useful.
 */
function _orGroup(pStanzas)
{
	let tmpStanzas = (pStanzas || []).filter(Boolean);
	if (!tmpStanzas.length) { return ''; }
	if (tmpStanzas.length === 1) { return tmpStanzas[0]; }
	return [_stanza('FOP', '', '', '')].concat(tmpStanzas).concat([_stanza('FCP', '', '', '')]).join('~');
}

/**
 * Columns meadow stamps on every table in this schema, so they can be asked for whatever a descriptor
 * chooses to render. A list that shows no Updated column still has an activity window over UpdateDate.
 */
const _AUDIT_FIELDS = { CreateDate: true, UpdateDate: true };

/**
 * The column each entry in a descriptor's Filter.Facets reads. A hand-written Filter config is the
 * only evidence some of these columns exist at all: WorkItem renders no WorkflowStatus column and
 * Feedback renders neither Addressed nor IDKeystoneCustomer, yet all three are real and all three are
 * facets those two lists offer. Reading the facet list is therefore part of reading the declaration,
 * not a convenience.
 */
const _FACET_FIELD =
{
	Status: 'Status',
	Type: 'Type',
	Priority: 'Priority',
	WorkflowStatus: 'WorkflowStatus',
	SourceType: 'SourceType',
	Customer: 'IDKeystoneCustomer',
	Addressed: 'Addressed',
	Assignee: 'IDAssignedUser',
	Target: 'TargetType'
};

/**
 * Whether the server can be asked about a field on this entity, per what the descriptor DECLARES.
 *
 * Four kinds of evidence, every one of them a declaration rather than a guess:
 *   - the audit columns meadow puts on every table;
 *   - the identity and number columns, which exist by construction (the descriptor is addressed by
 *     IDField and marks are formed from NumberField);
 *   - a rendered column that is not Computed, or a form field, both of which name real columns --
 *     Fields especially, since save() posts them straight back;
 *   - a facet the descriptor's own filter config offers.
 *
 * Anything else is unknown, and unknown must mean "ask locally" rather than "send it and see". The
 * cost of being wrong here is not a wrong answer, it is a 500 on a query Postgres will not compile.
 */
function _askable(pDescriptor, pField)
{
	let tmpDescriptor = pDescriptor || {};
	let tmpField = String(pField || '');
	if (!tmpField) { return false; }
	if (_AUDIT_FIELDS[tmpField]) { return true; }
	if (tmpField === tmpDescriptor.IDField) { return true; }
	if (tmpField === tmpDescriptor.NumberField) { return true; }
	if ((tmpDescriptor.Columns || []).some((pColumn) => pColumn && (pColumn.F === tmpField) && !pColumn.Computed)) { return true; }
	if ((tmpDescriptor.Fields || []).some((pFormField) => pFormField && (pFormField.F === tmpField))) { return true; }
	let tmpFacets = (tmpDescriptor.Filter && tmpDescriptor.Filter.Facets) || [];
	return tmpFacets.some((pFacet) => _FACET_FIELD[pFacet] === tmpField);
}

/** The fields a text search looks in, on an entity that has them. A row lacking one never matches on it. */
const _SEARCH_FIELDS = ['Title', 'Name', 'Summary', 'Description', 'Body'];

/** Which of those this entity actually declares. Empty means its search cannot go to the server at all. */
function searchFields(pDescriptor)
{
	return _SEARCH_FIELDS.filter((pField) => _askable(pDescriptor, pField));
}

/**
 * Sort stanzas for a list.
 *
 * ALWAYS TIEBREAKS ON THE PRIMARY KEY. Two rows with the same UpdateDate have no defined order in SQL,
 * and an undefined order across a page boundary means a row can appear on two pages or on none. That
 * is invisible in a 3-row fixture and routine in 1,075.
 */
function sortStanzas(pSort, pDescriptor)
{
	let tmpDescriptor = pDescriptor || {};
	let tmpIDField = tmpDescriptor.IDField || '';
	let tmpField = (pSort && pSort.Field) || '';
	// ACCEPTS Dir OR Direction. The list view stores its sort as { Field, Dir } (PictView-EntityList
	// sortBy), while a descriptor's DefaultSort parses to { Field, Direction }. Reading only one of
	// them makes every sort from the other source silently ascending -- the same shape of silent wrong
	// answer as a three-term FSF stanza, and just as invisible on a three-row fixture.
	let tmpRaw = String((pSort && (pSort.Direction || pSort.Dir)) || '').toLowerCase();
	let tmpDirection = ((tmpRaw === 'desc') || (tmpRaw === 'descending')) ? 'DESC' : 'ASC';
	let tmpOut = [];

	// A mark column (WI-019) is composed in the browser from the entity's number field, so ordering by
	// it means ordering by that number. Without this the one column people most often sort by is the
	// one that cannot go server-side.
	if ((tmpField === 'Designator') || (tmpField === 'MarkNumber'))
	{
		tmpField = tmpDescriptor.NumberField || '';
	}

	if (tmpField) { tmpOut.push(_stanza('FSF', tmpField, tmpDirection, '')); }
	if (tmpIDField && (tmpField !== tmpIDField)) { tmpOut.push(_stanza('FSF', tmpIDField, tmpDirection, '')); }
	return tmpOut;
}

/**
 * Which sorts cannot be asked of the server.
 *
 * Only one exists today: a WorkItem's session count is a cross-entity aggregate stamped onto rows in
 * the browser, so there is no column to order by. Named rather than inferred, because the honest
 * answer is to fetch everything for that one column, not to pretend it sorted.
 */
function sortIsExpressible(pSort, pDescriptor)
{
	let tmpDescriptor = pDescriptor || {};
	let tmpField = (pSort && pSort.Field) || '';
	if (!tmpField) { return true; }
	if ((tmpField === 'Designator') || (tmpField === 'MarkNumber'))
	{
		return !!tmpDescriptor.NumberField;
	}
	// ASKS THE DESCRIPTOR, NOT THE NAME. An earlier version of this read the leading underscore as a
	// convention for "client-derived", which caught WorkItem._SessionCount and silently missed
	// NodeRegistration's Roles and Workspaces -- both composed in the browser from CapabilityJSON, and
	// neither a column in the database. Sorting on either would have asked Postgres to ORDER BY a
	// column that does not exist. A naming convention is not a contract; the declaration is.
	let tmpColumn = (tmpDescriptor.Columns || []).find((pCol) => pCol && (pCol.F === tmpField));
	if (tmpColumn && tmpColumn.Computed) { return false; }
	if (tmpField.charAt(0) === '_') { return false; }
	// And it has to be a column of THIS entity. A sort survives in the URL hash and in saved list
	// preferences, neither of which is scoped to a screen, so a field name from another list can
	// arrive here intact; ORDER BY on a column that does not exist takes the whole query down.
	return _askable(tmpDescriptor, tmpField);
}

/**
 * Build the filter for a state.
 *
 * Returns { Filter, Unexpressed }. Unexpressed is a list of facet names this could not put into the
 * query; a non-empty Unexpressed means the caller MUST NOT page server-side, because the server would
 * be answering a different question than the screen is asking.
 */
function build(pState, pContext, pDescriptor, pSort)
{
	let tmpState = pState || {};
	let tmpContext = pContext || { CurrentUserID: 0, Agents: {} };
	let tmpDescriptor = pDescriptor || {};
	let tmpParts = [];
	let tmpUnexpressed = [];

	let fList = (pValue) => String(pValue == null ? '' : pValue).split(',').map((s) => s.trim()).filter(Boolean);

	// EVERY FACET GOES THROUGH HERE. A facet whose column this entity does not declare is reported
	// rather than sent: filter state is restored from the URL hash and from saved preferences, and
	// neither is scoped to the screen it came from, so a facet from another list can arrive intact.
	let fFacet = (pName, pField, fStanza) =>
	{
		if (!_askable(tmpDescriptor, pField)) { tmpUnexpressed.push(pName); return; }
		let tmpStanza = fStanza();
		if (tmpStanza) { tmpParts.push(tmpStanza); }
	};

	if (tmpState.status)
	{
		let tmpSet = fList(tmpState.status);
		if (tmpSet.length) { fFacet('status', 'Status', () => _stanza('FBL', 'Status', 'INN', tmpSet.map(_safe).join(','))); }
	}
	if (tmpState.type)
	{
		let tmpSet = fList(tmpState.type);
		if (tmpSet.length) { fFacet('type', 'Type', () => _stanza('FBL', 'Type', 'INN', tmpSet.map(_safe).join(','))); }
	}
	if (tmpState.priority) { fFacet('priority', 'Priority', () => _stanza('FBV', 'Priority', 'EQ', _safe(tmpState.priority))); }
	if (tmpState.wfstatus) { fFacet('wfstatus', 'WorkflowStatus', () => _stanza('FBV', 'WorkflowStatus', 'EQ', _safe(tmpState.wfstatus))); }
	if (tmpState.sourcetype) { fFacet('sourcetype', 'SourceType', () => _stanza('FBV', 'SourceType', 'EQ', _safe(tmpState.sourcetype))); }

	if (tmpState.target)
	{
		// 'none' means the row points at nothing, which the client reads as an empty or literal 'None'.
		if (String(tmpState.target) === 'none') { tmpUnexpressed.push('target-none'); }
		else { fFacet('target', 'TargetType', () => _stanza('FBV', 'TargetType', 'EQ', _safe(tmpState.target))); }
	}

	if (tmpState.customer)
	{
		fFacet('customer', 'IDKeystoneCustomer', () =>
		{
			let tmpValue = (String(tmpState.customer) === 'none') ? '0' : _safe(tmpState.customer);
			return _stanza('FBV', 'IDKeystoneCustomer', 'EQ', tmpValue);
		});
	}

	// 'no' means NOT truthy, which in this schema includes 0 and NULL. `NE 1` drops NULL rows in SQL,
	// so this one goes to the caller rather than quietly losing every un-stamped row. Checked before
	// the column is, because it is unexpressible on the one entity that HAS the column.
	if (tmpState.addressed === 'no') { tmpUnexpressed.push('addressed-open'); }
	else if (tmpState.addressed === 'yes') { fFacet('addressed', 'Addressed', () => _stanza('FBV', 'Addressed', 'EQ', '1')); }

	if (tmpState.assignee)
	{
		let tmpWho = String(tmpState.assignee);
		// 'ai' and 'human' are resolved through a roster the list holds, not through a column.
		// Expressible in principle as an id list; deferred rather than half-done. Named before the
		// column check so the report says which assignee filter could not be asked for.
		if ((tmpWho !== 'me') && (tmpWho !== 'unassigned') && !/^[0-9]+$/.test(tmpWho)) { tmpUnexpressed.push('assignee-' + tmpWho); }
		else
		{
			fFacet('assignee', 'IDAssignedUser', () =>
			{
				if (tmpWho === 'me') { return _stanza('FBV', 'IDAssignedUser', 'EQ', Number(tmpContext.CurrentUserID) || 0); }
				if (tmpWho === 'unassigned') { return _stanza('FBV', 'IDAssignedUser', 'EQ', '0'); }
				return _stanza('FBV', 'IDAssignedUser', 'EQ', tmpWho);
			});
		}
	}

	if (tmpState.recent)
	{
		let tmpMs = Number(tmpState.RecentMs) || 0;
		// RecentMs is resolved by the caller (FocusFilters.recentMs); a window this module was not told
		// the length of is a window it cannot express, and guessing one would narrow the list wrongly.
		if (tmpMs <= 0) { tmpUnexpressed.push('recent'); }
		else { fFacet('recent', 'UpdateDate', () => _stanza('FBV', 'UpdateDate', 'GT', new Date(Date.now() - tmpMs).toISOString())); }
	}

	// The activity lens keeps in-flight lanes always, plus anything touched recently. That is an OR of
	// a status list and a date, which is expressible -- and wrapped, like every other OR here.
	if (String(tmpState.activity || '') === 'related') { tmpUnexpressed.push('activity'); }

	let tmpQuery = String(tmpState.q || '').trim();
	if (tmpQuery)
	{
		let tmpLike = '%' + _safe(tmpQuery) + '%';
		// ONLY THE PROSE COLUMNS THIS ENTITY HAS. One list of the twenty-one has a Body column and one
		// has both Title and Description; searching the fixed five would name a column Postgres has
		// never heard of on nearly every list in the app, and that is a 500 rather than a thin result.
		let tmpOr = searchFields(tmpDescriptor).map((pField, pIndex) =>
			_stanza(pIndex ? 'FBVOR' : 'FBV', pField, 'LK', tmpLike));
		// A search naming a mark ("WI-19", "19") should find it, and the mark is not in the prose.
		let tmpNumber = tmpQuery.replace(/^[A-Za-z]+-?/, '');
		if (tmpDescriptor.NumberField && /^[0-9]+$/.test(tmpNumber))
		{
			tmpOr.push(_stanza(tmpOr.length ? 'FBVOR' : 'FBV', tmpDescriptor.NumberField, 'EQ', tmpNumber));
		}
		// An entity with no searchable column and no mark cannot answer a search at all. Saying so is
		// the difference between "no rows match" and "this question was never asked".
		if (!tmpOr.length) { tmpUnexpressed.push('search'); }
		else { tmpParts.push(_orGroup(tmpOr)); }
	}

	(sortStanzas(pSort, tmpDescriptor)).forEach((pStanza) => tmpParts.push(pStanza));
	if (!sortIsExpressible(pSort, tmpDescriptor)) { tmpUnexpressed.push('sort-' + ((pSort || {}).Field || '')); }

	return { Filter: tmpParts.filter(Boolean).join('~'), Unexpressed: tmpUnexpressed };
}

module.exports = { build, sortStanzas, sortIsExpressible, searchFields, _askable, _orGroup, _stanza, SEARCH_FIELDS: _SEARCH_FIELDS };
